import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";

/**
 * Privacy Policy + Terms of Service — combined into a single LegalPage
 * component that renders one or the other based on `kind`. Required for
 * Play Store submission (Google bots fetch these URLs and verify content).
 */
function LegalShell({ title, children }) {
  const navigate = useNavigate();
  // Use history-back so a logged-in user opening Privacy from the in-app
  // Menu returns to wherever they were (Dashboard, Themes, Store…) instead
  // of being bounced to "/" which then sometimes lands on /login.
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/dashboard");
  };
  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2C332A]" data-testid={`legal-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <header className="px-4 pt-3 pb-3 flex items-center gap-3 border-b border-[#E6E2D8] bg-white/80 backdrop-blur">
        <button
          type="button"
          onClick={goBack}
          className="rounded-full p-2 hover:bg-[#E6E2D8]/60"
          aria-label="Back"
          data-testid="legal-back-btn"
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
        </button>
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl font-light tracking-tight">
          {title}
        </h1>
      </header>
      <main className="max-w-2xl mx-auto px-5 py-8 prose prose-sm prose-stone leading-relaxed">
        {children}
      </main>
      <footer className="text-center text-xs text-[#2C332A]/50 pb-10">
        © {new Date().getFullYear()} Fitjong · <Link to="/" className="underline">fitjong.co.uk</Link>
      </footer>
    </div>
  );
}

export function PrivacyPolicy() {
  const { i18n } = useTranslation();
  const isPL = (i18n.language || "en").toLowerCase().startsWith("pl");
  const dateStr = new Date().toLocaleDateString(isPL ? "pl-PL" : "en-GB", {
    year: "numeric", month: "long", day: "numeric",
  });

  if (isPL) {
    return (
      <LegalShell title="Polityka prywatności">
        <p className="text-[#2C332A]/60">Ostatnia aktualizacja: {dateStr}</p>

        <h2>1. Kim jesteśmy</h2>
        <p>
          Fitjong („my", „nas", „nasze") prowadzi aplikację mobilną Mahjong solitaire
          o tematyce fitness, dostępną w Google Play oraz Apple App Store. Niniejsza
          polityka wyjaśnia, jakie dane zbieramy podczas Twojej gry oraz w jaki sposób
          ich używamy.
        </p>

        <h2>2. Jakie dane zbieramy</h2>
        <ul>
          <li><b>Dane konta</b> — adres e-mail oraz nazwa wyświetlana podczas rejestracji. Jeśli logujesz się przez Google, otrzymujemy jedynie Twoje imię, adres e-mail oraz adres URL zdjęcia profilowego.</li>
          <li><b>Dane rozgrywki</b> — ukończone poziomy, czas gry, dopasowane kafelki, szacowane spalone kalorie, dzienna passa, energia, saldo monet oraz posiadane wzmacniacze. Wszystko przechowywane jest pod Twoim kontem.</li>
          <li><b>Historia zakupów</b> — przy zakupie pakietu monet lub usunięciu reklam otrzymujemy token zamówienia z Google Play / Stripe. Zapisujemy identyfikator zamówienia, SKU, kwotę i datę — nigdy danych karty.</li>
          <li><b>Dane urządzenia</b> — w momencie wczytania reklam (AdMob), Google może pobrać identyfikator reklamowy urządzenia, adres IP oraz przybliżoną lokalizację w celu personalizacji reklam. Identyfikator ten możesz w każdej chwili zresetować w ustawieniach urządzenia.</li>
        </ul>

        <h2>3. W jakim celu używamy danych</h2>
        <ul>
          <li>Aby umożliwić rozgrywkę i zapisywać Twój postęp między sesjami.</li>
          <li>Aby przetwarzać zakupy w aplikacji za pośrednictwem Google Play Billing lub Stripe.</li>
          <li>Aby wyświetlać reklamy za pośrednictwem Google AdMob (bez mediacji).</li>
          <li>Aby wykrywać oszustwa i próby ponownego użycia tokenów zakupowych.</li>
          <li><b>Nigdy</b> nie sprzedajemy Twoich danych.</li>
        </ul>

        <h2>4. Strony trzecie</h2>
        <ul>
          <li><b>Google Play Billing</b> — obsługa zakupów na urządzeniach z Androidem.</li>
          <li><b>Google AdMob</b> — reklamy nagradzane oraz pełnoekranowe. Obowiązuje polityka prywatności AdMob: <a href="https://policies.google.com/privacy">policies.google.com/privacy</a>.</li>
          <li><b>Stripe</b> — płatności w opcjonalnej wersji webowej.</li>
          <li><b>MongoDB Atlas</b> — zaszyfrowany hosting bazy danych (region UE).</li>
        </ul>

        <h2>5. Dzieci</h2>
        <p>
          Fitjong jest grą dla każdego, jednak świadomie nie zbieramy danych od dzieci
          poniżej 13. roku życia. Jeśli masz mniej niż 13 lat, prosimy o niezakładanie
          konta.
        </p>

        <h2>6. Twoje prawa</h2>
        <p>
          W dowolnym momencie możesz poprosić o kopię swoich danych, ich usunięcie lub
          zamknięcie konta, pisząc na adres{" "}
          <a href="mailto:privacy@fitjong.co.uk">privacy@fitjong.co.uk</a>. Odpowiadamy
          w ciągu 30 dni.
        </p>

        <h2>7. Okres przechowywania danych</h2>
        <p>
          Dane konta i rozgrywki przechowujemy do momentu otrzymania prośby o ich
          usunięcie lub przez 3 lata nieaktywności. Historia zakupów przechowywana
          jest przez 7 lat w związku z obowiązkami podatkowymi (regulacje UK / UE).
        </p>

        <h2>8. Zmiany</h2>
        <p>
          W przypadku istotnych zmian niniejszej polityki wyświetlimy stosowną
          informację w aplikacji przy kolejnym uruchomieniu Fitjong. Data
          „Ostatnia aktualizacja" u góry zostanie odpowiednio uaktualniona.
        </p>

        <h2>9. Kontakt</h2>
        <p>
          Pytania? <a href="mailto:privacy@fitjong.co.uk">privacy@fitjong.co.uk</a>
        </p>
      </LegalShell>
    );
  }

  return (
    <LegalShell title="Privacy Policy">
      <p className="text-[#2C332A]/60">Last updated: {dateStr}</p>

      <h2>1. Who we are</h2>
      <p>
        Fitjong ("we", "us", "our") operates the fitness-themed Mahjong solitaire mobile
        application available on Google Play and the Apple App Store. This policy explains
        what data we collect when you play and how we use it.
      </p>

      <h2>2. Information we collect</h2>
      <ul>
        <li><b>Account info</b> — email address and a display name when you sign up. If you sign in with Google, we receive only your name, email, and profile picture URL.</li>
        <li><b>Gameplay data</b> — levels played, time taken, matches made, calories estimated, daily streak, energy, coin balance, boosters owned. All stored under your account.</li>
        <li><b>Purchase records</b> — when you buy coin packs or remove ads, we receive an order token from Google Play / Stripe. We store the order ID, SKU, amount, and date — never your card details.</li>
        <li><b>Device info</b> — when ads load (AdMob), Google may collect a non-resettable advertising ID, IP address, and approximate location for ad personalisation. You can reset this ID in your device settings.</li>
      </ul>

      <h2>3. How we use it</h2>
      <ul>
        <li>To run the game and save your progress between sessions.</li>
        <li>To process in-app purchases via Google Play Billing or Stripe.</li>
        <li>To show ads via Google AdMob (Mediation: none).</li>
        <li>To detect cheating / replay-attack of purchase tokens.</li>
        <li>We do <b>not</b> sell your data, ever.</li>
      </ul>

      <h2>4. Third parties</h2>
      <ul>
        <li><b>Google Play Billing</b> — purchase processing on Android.</li>
        <li><b>Google AdMob</b> — rewarded + interstitial ads. AdMob's privacy policy applies: <a href="https://policies.google.com/privacy">policies.google.com/privacy</a>.</li>
        <li><b>Stripe</b> — payments on the optional web version.</li>
        <li><b>MongoDB Atlas</b> — encrypted database hosting (EU region).</li>
      </ul>

      <h2>5. Children</h2>
      <p>
        Fitjong is rated for everyone but we do not knowingly collect data from children
        under 13. If you are under 13, please do not register an account.
      </p>

      <h2>6. Your rights</h2>
      <p>
        You can request a copy of your data, ask us to delete it, or close your account at
        any time by emailing <a href="mailto:privacy@fitjong.co.uk">privacy@fitjong.co.uk</a>.
        We respond within 30 days.
      </p>

      <h2>7. Data retention</h2>
      <p>
        Account + gameplay data are kept until you request deletion or 3 years of inactivity.
        Purchase records are retained 7 years for tax purposes (UK / EU regulatory requirement).
      </p>

      <h2>8. Changes</h2>
      <p>
        If we materially change this policy we'll show an in-app notice the next time you
        open Fitjong. The "Last updated" date at the top will reflect the change.
      </p>

      <h2>9. Contact</h2>
      <p>
        Questions? <a href="mailto:privacy@fitjong.co.uk">privacy@fitjong.co.uk</a>
      </p>
    </LegalShell>
  );
}

export function TermsOfService() {
  const { i18n } = useTranslation();
  const isPL = (i18n.language || "en").toLowerCase().startsWith("pl");
  const dateStr = new Date().toLocaleDateString(isPL ? "pl-PL" : "en-GB", {
    year: "numeric", month: "long", day: "numeric",
  });

  if (isPL) {
    return (
      <LegalShell title="Regulamin">
        <p className="text-[#2C332A]/60">Ostatnia aktualizacja: {dateStr}</p>

        <h2>1. Akceptacja</h2>
        <p>
          Instalując lub korzystając z aplikacji Fitjong, akceptujesz niniejszy regulamin.
          Jeśli się z nim nie zgadzasz, prosimy o odinstalowanie aplikacji.
        </p>

        <h2>2. Twoje konto</h2>
        <p>
          Jesteś odpowiedzialny za zachowanie hasła w bezpieczeństwie oraz za wszystko,
          co dzieje się na Twoim koncie. Prosimy nie udostępniać kont — jedna osoba,
          jedno konto.
        </p>

        <h2>3. Zakupy w aplikacji</h2>
        <ul>
          <li>Pakiety monet, opcja „Usuń reklamy" oraz Pro Fitness Pack to cyfrowe dobra bezzwrotne, sprzedawane przez Google Play Billing (Android) lub Stripe (web).</li>
          <li>Waluta wyświetlana jest w USD; Twój sklep pobierze opłatę w lokalnej walucie po kursie ustalonym przez Google/Stripe.</li>
          <li>Monety nie mają realnej wartości pieniężnej, nie można ich przenosić między kontami i przepadają w przypadku zamknięcia konta.</li>
          <li>Jeśli uważasz, że opłata została naliczona błędnie, skontaktuj się z pomocą techniczną Google Play lub Stripe — my nie możemy bezpośrednio zwrócić pieniędzy.</li>
        </ul>

        <h2>4. Akceptowalne użytkowanie</h2>
        <p>Zobowiązujesz się nie:</p>
        <ul>
          <li>Inżynierii odwrotnej, modyfikacji ani dystrybucji aplikacji.</li>
          <li>Używania botów / skryptów / zmodyfikowanych klientów do zdobywania monet lub passy.</li>
          <li>Próbowania ominięcia weryfikacji zakupów lub powtarzania transakcji.</li>
          <li>Zgłaszania nieprawdziwych roszczeń dotyczących zakupów, które zostały zrealizowane.</li>
        </ul>
        <p>Możemy zawiesić lub zamknąć konta naruszające te zasady bez uprzedzenia.</p>

        <h2>5. Zasady energii i passy</h2>
        <p>
          Energia regeneruje się 1 jednostka na godzinę, do maksymalnie 10. Passa resetuje
          się po przeoczeniu pełnego dnia kalendarzowego w Twojej strefie czasowej, chyba
          że obejrzysz reklamę nagradzaną typu „przywróć passę" w ciągu 24 godzin
          od jej zerwania.
        </p>

        <h2>6. Reklamy</h2>
        <p>
          Bezpłatna rozgrywka jest finansowana z reklam Google AdMob (nagradzanych oraz
          pełnoekranowych). Możesz je wyłączyć kupując pakiet „Usuń reklamy". Nasza
          aplikacja nie decyduje, które konkretnie reklamy Google wyświetla.
        </p>

        <h2>7. Brak porad fitness</h2>
        <p>
          Fitjong to gra logiczna w estetyce fitness. Liczba „spalonych kalorii" jest
          zabawnym wskaźnikiem w grze, <b>NIE</b> rzeczywistym oszacowaniem kalorii.
          Nazwy kafelków nie są profesjonalnymi instrukcjami ćwiczeń. Jeśli zaczynasz
          rzeczywisty plan treningowy, najpierw skonsultuj się z wykwalifikowanym trenerem
          lub lekarzem.
        </p>

        <h2>8. Odpowiedzialność</h2>
        <p>
          Fitjong jest dostarczany „taki, jaki jest". Nie ponosimy odpowiedzialności za
          żadne pośrednie ani następcze straty wynikające z przestojów aplikacji,
          problemów z reklamami lub tymczasowej utraty postępu w grze poza ostatnią kopią
          zapasową.
        </p>

        <h2>9. Zakończenie</h2>
        <p>
          Możesz w każdej chwili przestać korzystać z Fitjong, odinstalowując aplikację.
          Możemy zakończyć Twój dostęp, jeśli naruszysz niniejszy regulamin. Sekcje 3, 7,
          8 obowiązują również po zakończeniu.
        </p>

        <h2>10. Prawo właściwe</h2>
        <p>
          Niniejszy regulamin podlega prawu Anglii i Walii. Wszelkie spory będą
          rozstrzygane przez sądy angielskie.
        </p>

        <h2>11. Kontakt</h2>
        <p><a href="mailto:legal@fitjong.co.uk">legal@fitjong.co.uk</a></p>
      </LegalShell>
    );
  }

  return (
    <LegalShell title="Terms of Service">
      <p className="text-[#2C332A]/60">Last updated: {dateStr}</p>

      <h2>1. Acceptance</h2>
      <p>
        By installing or using Fitjong you agree to these terms. If you don't agree, please
        uninstall the app.
      </p>

      <h2>2. Your account</h2>
      <p>
        You're responsible for keeping your password safe and for everything that happens
        under your account. Please don't share accounts. One person, one account.
      </p>

      <h2>3. In-app purchases</h2>
      <ul>
        <li>Coin packs, "Remove Ads", and the Pro Fitness Pack are non-refundable digital goods sold through Google Play Billing (Android) or Stripe (web).</li>
        <li>All currency is shown in USD; your store will charge in your local currency at the rate Google/Stripe sets.</li>
        <li>Coins have no real-world cash value, cannot be transferred between accounts, and are forfeited if your account is closed.</li>
        <li>If you believe a charge was made in error, contact Google Play or Stripe support — we cannot refund directly.</li>
      </ul>

      <h2>4. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Reverse engineer, modify, or distribute the app.</li>
        <li>Use bots / scripts / modified clients to gain coins or streak.</li>
        <li>Attempt to bypass purchase verification or replay receipts.</li>
        <li>Submit false claims about purchases that did complete.</li>
      </ul>
      <p>We may suspend or close accounts that violate these rules without notice.</p>

      <h2>5. Energy &amp; streak rules</h2>
      <p>
        Energy regenerates 1 unit per hour, capped at 10. Streaks reset when you miss a
        full calendar day in your local time zone, except when you watch a "streak restore"
        rewarded ad within 24 h of breaking it.
      </p>

      <h2>6. Ads</h2>
      <p>
        Free play is supported by Google AdMob ads (rewarded + interstitial). You can
        remove them by purchasing the "Remove Ads" pack. Our app does not control which
        specific ads Google chooses to show.
      </p>

      <h2>7. No fitness advice</h2>
      <p>
        Fitjong is a casual puzzle game with a fitness aesthetic. The "calories burned"
        number is a fun in-game metric, <b>NOT</b> a real-world calorie estimate. Tile names
        are not professional exercise instructions. If you start a real workout routine,
        consult a qualified trainer or doctor first.
      </p>

      <h2>8. Liability</h2>
      <p>
        Fitjong is provided "as is". We're not liable for any indirect or consequential
        loss arising from app downtime, ad-mediation issues, or temporary loss of in-game
        progress beyond the most recent backup.
      </p>

      <h2>9. Termination</h2>
      <p>
        You can stop using Fitjong any time by uninstalling it. We can terminate your
        access if you breach these terms. Sections 3, 7, 8 survive termination.
      </p>

      <h2>10. Governing law</h2>
      <p>
        These terms are governed by the laws of England and Wales. Any dispute will be
        handled in the English courts.
      </p>

      <h2>11. Contact</h2>
      <p><a href="mailto:legal@fitjong.co.uk">legal@fitjong.co.uk</a></p>
    </LegalShell>
  );
}
